import { useCallback, useRef, useState } from 'react'

import axios from 'axios'

import {
  AudioFileUploadFileInfo,
  AudioFileUploadStatus,
} from 'src/Components/AudioFileUpload'
import { AUDIO_FILE_ERROR_MESSAGES } from 'src/Components/AudioFileUpload/validation'
import { useAppDispatch } from 'src/Hooks/redux'
import {
  SpotifyCampaignTrackFile,
  spotifyCampaignStatusApiUtil,
} from 'src/Redux/spotifyCampaignStatusApi'
import {
  deletePreReleaseTrackFile,
  uploadPreReleaseTrackFile,
} from 'src/Services/preReleaseTrackFile'

export interface UseTrackFileUploadParams {
  campaignId: string
  savedFile: SpotifyCampaignTrackFile | null
  onSaved: () => void
}

export interface UseTrackFileUploadResult {
  file: AudioFileUploadFileInfo | null
  status: AudioFileUploadStatus
  progress: number
  error: string | null
  isSaving: boolean
  canSave: boolean
  selectFile: (file: File) => void
  removeFile: () => void
  saveFile: () => void
}

const toFileInfo = (
  savedFile: SpotifyCampaignTrackFile | null,
): AudioFileUploadFileInfo | null => {
  if (!savedFile) {
    return null
  }
  return {
    name: savedFile.name,
    ...(savedFile.sizeBytes === null ? {} : { size: savedFile.sizeBytes }),
  }
}

const getUploadErrorMessage = (error: unknown): string => {
  if (axios.isAxiosError<{ reason?: string }>(error)) {
    return error.response?.data.reason || AUDIO_FILE_ERROR_MESSAGES.uploadFailed
  }
  return AUDIO_FILE_ERROR_MESSAGES.uploadFailed
}

export const useTrackFileUpload = ({
  campaignId,
  savedFile,
  onSaved,
}: UseTrackFileUploadParams): UseTrackFileUploadResult => {
  const dispatch = useAppDispatch()
  const [file, setFile] = useState<AudioFileUploadFileInfo | null>(() =>
    toFileInfo(savedFile),
  )
  const [status, setStatus] = useState<AudioFileUploadStatus>(() =>
    savedFile ? 'uploaded' : 'idle',
  )
  const [progress, setProgress] = useState(0)
  const [error, setError] = useState<string | null>(null)

  const [isSaving, setIsSaving] = useState(false)
  const [hasRemovedFile, setHasRemovedFile] = useState(false)
  const [hasUserInteracted, setHasUserInteracted] = useState(false)
  const hasStoredFileRef = useRef(Boolean(savedFile))
  const uploadIdRef = useRef(0)

  const refreshCampaign = useCallback(() => {
    dispatch(
      spotifyCampaignStatusApiUtil.invalidateTags(['SpotifyCampaignDetails']),
    )
  }, [dispatch])

  const clearCachedTrackFile = useCallback(() => {
    dispatch(
      spotifyCampaignStatusApiUtil.updateQueryData(
        'getSpotifyCampaignStatus',
        Number(campaignId),
        (draft) => {
          draft.track.audioUrl = null
        },
      ),
    )
  }, [campaignId, dispatch])

  const selectFile = useCallback(
    (selectedFile: File) => {
      uploadIdRef.current += 1
      const uploadId = uploadIdRef.current
      setHasUserInteracted(true)
      setError(null)
      setProgress(0)
      setFile({ name: selectedFile.name, size: selectedFile.size })
      setStatus('uploading')

      const run = async (): Promise<void> => {
        if (hasStoredFileRef.current || savedFile) {
          await deletePreReleaseTrackFile(campaignId)
          hasStoredFileRef.current = false
          setHasRemovedFile(true)
          clearCachedTrackFile()
        }
        await uploadPreReleaseTrackFile({
          campaignId,
          file: selectedFile,
          onProgress: (percentage) => {
            if (uploadId === uploadIdRef.current) {
              setProgress(percentage)
            }
          },
        })

        if (uploadId !== uploadIdRef.current) {
          await deletePreReleaseTrackFile(campaignId)
          setHasRemovedFile(true)
          refreshCampaign()
          return
        }
        hasStoredFileRef.current = true
        setStatus('uploaded')
        refreshCampaign()
      }

      void run().catch((uploadError: unknown) => {
        if (uploadId !== uploadIdRef.current) {
          return
        }
        setFile(null)
        setStatus('idle')
        setProgress(0)
        setError(getUploadErrorMessage(uploadError))
      })
    },
    [campaignId, clearCachedTrackFile, refreshCampaign, savedFile],
  )

  const removeFile = useCallback(() => {
    uploadIdRef.current += 1
    setHasUserInteracted(true)
    setFile(null)
    setStatus('idle')
    setProgress(0)
    setError(null)

    if (!hasStoredFileRef.current && !savedFile) {
      return
    }
    hasStoredFileRef.current = false
    clearCachedTrackFile()
    void deletePreReleaseTrackFile(campaignId)
      .then(() => {
        setHasRemovedFile(true)
        refreshCampaign()
      })
      .catch(() => {
        setError(AUDIO_FILE_ERROR_MESSAGES.uploadFailed)
        refreshCampaign()
      })
  }, [campaignId, clearCachedTrackFile, refreshCampaign, savedFile])

  const currentFile = hasUserInteracted ? file : toFileInfo(savedFile)
  let currentStatus: AudioFileUploadStatus
  if (hasUserInteracted) {
    currentStatus = status
  } else if (savedFile) {
    currentStatus = 'uploaded'
  } else {
    currentStatus = 'idle'
  }

  const canSave =
    currentStatus === 'uploaded' || (currentStatus === 'idle' && hasRemovedFile)

  const saveFile = useCallback(() => {
    if (!canSave) {
      return
    }
    setIsSaving(true)
    refreshCampaign()
    onSaved()
  }, [canSave, onSaved, refreshCampaign])

  return {
    file: currentFile,
    status: currentStatus,
    progress,
    error,
    isSaving,
    selectFile,
    removeFile,
    saveFile,
    canSave,
  }
}
